'use client'

import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/use-toast'
import {
  Plus,
  Trash2,
  BookOpen,
  LightbulbIcon,
  Users,
  Copy,
  LayoutGrid,
  ListFilter,
  MoreVertical,
  Settings2,
  Search,
  Check,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from 'react-i18next'

interface Subject {
  id: string
  name: string
}

interface Teacher {
  id: string
  name: string
  subjects: string[]
}

interface Class {
  id: string
  name: string
}

interface Room {
  id: string
  name: string
  type?: string
}

interface Lesson {
  subject: string
  teacher: string
  class: string
  hours: number
  isGrouped?: boolean
  divide?: number[]
  alternateWith?: string
  room?: string
  divideInput?: string
}

interface LessonsConfigurationProps {
  lessons: Lesson[]
  subjects: Subject[]
  teachers: Teacher[]
  classes: Class[]
  rooms?: Room[]
  onAddLesson: (lesson: Lesson) => void
  onDeleteLesson: (index: number) => void
  onUpdateLessons?: (lessons: Lesson[]) => void
}

export function LessonsConfiguration({
  lessons,
  subjects,
  teachers,
  classes,
  onDeleteLesson,
  onUpdateLessons,
}: LessonsConfigurationProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.lessonsConfiguration' })
  const { toast } = useToast()

  // View state
  const [viewMode, setViewMode] = useState<'normal' | 'byClass'>('byClass')

  // Batch editing state
  const [selectedClass, setSelectedClass] = useState<string>('')

  // Batch configuration state
  const [batchConfigs, setBatchConfigs] = useState<Record<string, Partial<Lesson>>>({})
  const [searchQuery, setSearchQuery] = useState('')

  // Duplication state
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [sourceClassForDuplicate, setSourceClassForDuplicate] = useState<string | null>(null)
  const [targetClassIds, setTargetClassIds] = useState<string[]>([])

  // Room type selection
  const [roomTypes, setRoomTypes] = useState<string[]>([])

  // Validation toggle
  const [enforceValidation, setEnforceValidation] = useState<boolean>(false)

  // Initialize batch config when class changes
  useEffect(() => {
    if (selectedClass) {
      const classLessons = lessons.filter(l => l.class === selectedClass)
      const initialConfigs: Record<string, Partial<Lesson>> = {}

      classLessons.forEach(lesson => {
        initialConfigs[lesson.subject] = {
          teacher: lesson.teacher,
          hours: lesson.hours,
          room: lesson.room,
          isGrouped: lesson.isGrouped,
          divide: lesson.divide,
          alternateWith: lesson.alternateWith,
        }
      })

      setBatchConfigs(initialConfigs)
    } else {
      setBatchConfigs({})
    }
  }, [selectedClass, lessons])

  const handleBatchSave = () => {
    if (!selectedClass) return

    const newLessonsForClass: Lesson[] = Object.entries(batchConfigs)
      .filter(([_, config]) => config.teacher && config.hours && config.hours > 0)
      .map(([subjectId, config]) => ({
        class: selectedClass,
        subject: subjectId,
        teacher: config.teacher!,
        hours: config.hours!,
        room: config.room || 'room',
        isGrouped: config.isGrouped,
        divide: config.divide,
        alternateWith: config.alternateWith,
      }))

    const otherLessons = lessons.filter(l => l.class !== selectedClass)
    const updatedLessons = [...otherLessons, ...newLessonsForClass]

    if (onUpdateLessons) {
      onUpdateLessons(updatedLessons)
    } else {
      toast({
        title: "Warning",
        description: "Batch update not fully supported by parent component yet.",
        variant: "destructive"
      })
    }

    toast({
      title: t('errors.lessonAdded'),
      description: t('errors.lessonAddedDesc'),
    })
  }

  const handleDuplicate = () => {
    if (!sourceClassForDuplicate || targetClassIds.length === 0) return

    const sourceLessons = lessons.filter(l => l.class === sourceClassForDuplicate)
    const newLessons: Lesson[] = []

    targetClassIds.forEach(targetId => {
      sourceLessons.forEach(lesson => {
        newLessons.push({
          ...lesson,
          class: targetId
        })
      })
    })

    if (onUpdateLessons) {
      const filteredLessons = lessons.filter(l => !targetClassIds.includes(l.class))
      onUpdateLessons([...filteredLessons, ...newLessons])
    }

    setIsDuplicateDialogOpen(false)
    setTargetClassIds([])
    setSourceClassForDuplicate(null)

    toast({
      title: "Success",
      description: `Configuration duplicated to ${targetClassIds.length} classes.`,
    })
  }

  const fetchRoomTypes = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/classroom/etablissement`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      )

      if (response.ok) {
        const classrooms = await response.json()
        const types: string[] = []
        classrooms.forEach((room: any) => {
          if (room.type && typeof room.type === 'string' && !types.includes(room.type)) {
            types.push(room.type)
          }
        })

        if (types.length === 0) {
          setRoomTypes(['room'])
        } else {
          setRoomTypes(types)
        }
      }
    } catch (error) {
      // console.error('Error fetching room types:', error)
      setRoomTypes(['room'])
    }
  }

  useEffect(() => {
    fetchRoomTypes()
  }, [])

  const getSubjectName = (subjectId: string) => {
    return subjects.find((s) => s.id === subjectId)?.name || 'Unknown'
  }

  const getTeacherName = (teacherId: string) => {
    return teachers.find((t) => t.id === teacherId)?.name || 'Unknown'
  }

  const getClassName = (classId: string) => {
    return classes.find((c) => c.id === classId)?.name || 'Unknown'
  }

  const parseDivision = (input: string): number[] => {
    try {
      const numbers = input
        .split(',')
        .map((n) => Number.parseInt(n.trim()))
        .filter((n) => !isNaN(n))
      return numbers.length > 0 ? numbers : []
    } catch {
      return []
    }
  }

  const filteredSubjects = subjects.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('title')}</h2>
          <p className="text-slate-500">{t('description')}</p>
        </div>
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
          <Button
            variant={viewMode === 'byClass' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('byClass')}
            className={viewMode === 'byClass' ? 'shadow-sm' : ''}
          >
            <LayoutGrid className="h-4 w-4 mr-2" />
            {t('viewByClass') || 'By Class'}
          </Button>
          <Button
            variant={viewMode === 'normal' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('normal')}
            className={viewMode === 'normal' ? 'shadow-sm' : ''}
          >
            <ListFilter className="h-4 w-4 mr-2" />
            {t('viewNormal') || 'All Lessons'}
          </Button>
        </div>
      </div>

      {viewMode === 'byClass' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  {t('selectClass')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="p-2 space-y-1">
                    {classes.map((cls) => (
                      <button
                        key={cls.id}
                        onClick={() => setSelectedClass(cls.id)}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center justify-between group ${selectedClass === cls.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'hover:bg-slate-50 text-slate-600'
                          }`}
                      >
                        <span className="truncate">{cls.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {lessons.filter(l => l.class === cls.id).length}
                          </Badge>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-40 p-1" align="end">
                              <Button
                                variant="ghost"
                                className="w-full justify-start text-xs h-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSourceClassForDuplicate(cls.id);
                                  setIsDuplicateDialogOpen(true);
                                }}
                              >
                                <Copy className="h-3 w-3 mr-2" />
                                {t('duplicate') || 'Duplicate'}
                              </Button>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-9 space-y-6">
            {!selectedClass ? (
              <Card className="h-[500px] flex flex-col items-center justify-center text-center p-6 border-dashed">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">{t('noClassSelected') || 'Select a Class'}</h3>
                <p className="text-slate-500 max-w-xs">{t('selectClassToConfigure') || 'Choose a class from the sidebar to configure its subjects and teachers.'}</p>
              </Card>
            ) : (
              <Card className="border-blue-100 shadow-sm">
                <CardHeader className="border-b bg-slate-50/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {t('configuringFor') || 'Configuring'} {getClassName(selectedClass)}
                    </CardTitle>
                    <CardDescription>
                      {t('batchConfigDesc') || 'Select subjects and assign teachers for this class.'}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-md border text-sm flex-1 sm:flex-none justify-center sm:justify-start">
                      <Checkbox
                        id="enforce-validation-batch"
                        checked={enforceValidation}
                        onCheckedChange={(checked) => setEnforceValidation(checked === true)}
                      />
                      <Label htmlFor="enforce-validation-batch" className="cursor-pointer font-medium whitespace-nowrap">
                        {t('enforceValidation')}
                      </Label>
                    </div>
                    <Button onClick={handleBatchSave} className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none">
                      <Check className="h-4 w-4 mr-2" />
                      {t('saveChanges') || 'Save Changes'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="p-4 border-b bg-white flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="relative flex-1 w-full">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder={t('searchSubjects') || 'Search subjects...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-full"
                      />
                    </div>
                    <div className="text-sm text-slate-500 whitespace-nowrap">
                      {Object.keys(batchConfigs).length} {t('subjectsSelected') || 'subjects selected'}
                    </div>
                  </div>
                  <div className="overflow-x-auto w-full border-t">
                    <ScrollArea className="h-[600px]">
                      <div className="min-w-[900px]">
                        <Table>
                          <TableHeader className="sticky top-0 bg-white z-10">
                            <TableRow>
                              <TableHead className="w-[50px]"></TableHead>
                              <TableHead>{t('subject')}</TableHead>
                              <TableHead className="w-[250px]">{t('teacher')}</TableHead>
                              <TableHead className="w-[180px]">{t('roomType') || 'Room Type'}</TableHead>
                              <TableHead className="w-[100px]">{t('periodsWeek')}</TableHead>
                              <TableHead className="w-[120px]">{t('splitPattern') || 'Split (e.g. 2,1)'}</TableHead>
                              <TableHead className="w-[80px] text-right">{t('advanced') || 'Advanced'}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredSubjects.map((subject) => {
                              const config = batchConfigs[subject.id] || {};
                              const isEnabled = !!batchConfigs[subject.id];

                              const subjectTeachers = enforceValidation
                                ? teachers.filter(t => t.subjects.includes(subject.id))
                                : teachers;

                              return (
                                <TableRow key={subject.id} className={isEnabled ? 'bg-blue-50/30' : ''}>
                                  <TableCell>
                                    <Checkbox
                                      checked={isEnabled}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setBatchConfigs(prev => ({
                                            ...prev,
                                            [subject.id]: {
                                              hours: 1,
                                              room: 'room',
                                              teacher: subjectTeachers.length === 1 ? subjectTeachers[0].id : undefined
                                            }
                                          }))
                                        } else {
                                          const newConfigs = { ...batchConfigs };
                                          delete newConfigs[subject.id];
                                          setBatchConfigs(newConfigs);
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <BookOpen className="h-4 w-4 text-slate-400" />
                                      {subject.name}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Select
                                      disabled={!isEnabled}
                                      value={config.teacher || ''}
                                      onValueChange={(val) => setBatchConfigs(prev => ({
                                        ...prev,
                                        [subject.id]: { ...prev[subject.id], teacher: val }
                                      }))}
                                    >
                                      <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t('selectTeacherPlaceholder')} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {subjectTeachers.map(t => (
                                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    <Select
                                      disabled={!isEnabled}
                                      value={config.room || 'room'}
                                      onValueChange={(val) => setBatchConfigs(prev => ({
                                        ...prev,
                                        [subject.id]: { ...prev[subject.id], room: val }
                                      }))}
                                    >
                                      <SelectTrigger className="h-9">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {roomTypes.map(rt => (
                                          <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        min={1}
                                        max={10}
                                        disabled={!isEnabled}
                                        value={config.hours || 1}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value);
                                          if (!isNaN(val)) {
                                            setBatchConfigs(prev => ({
                                              ...prev,
                                              [subject.id]: { ...prev[subject.id], hours: val }
                                            }))
                                          }
                                        }}
                                        className="h-9 w-16 text-center"
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      placeholder="2,1"
                                      disabled={!isEnabled}
                                      value={config.divideInput !== undefined ? config.divideInput : (config.divide?.join(',') || '')}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const div = parseDivision(val);
                                        setBatchConfigs(prev => ({
                                          ...prev,
                                          [subject.id]: {
                                            ...prev[subject.id],
                                            divide: div,
                                            divideInput: val,
                                            isGrouped: div.length > 1
                                          }
                                        }))
                                      }}
                                      className="h-9 w-24 text-center font-mono text-xs"
                                    />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button variant="ghost" size="icon" disabled={!isEnabled} className="h-8 w-8">
                                          <Settings2 className="h-4 w-4" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80" align="end">
                                        <div className="space-y-4">
                                          <h4 className="font-medium leading-none">{t('alternatingSubjects') || 'Alternating Subjects'}</h4>
                                          <Separator />

                                          <div className="space-y-2">
                                            <Label className="text-sm font-medium">{t('alternateWith') || 'Alternate With'}</Label>
                                            <Select
                                              value={config.alternateWith || 'none'}
                                              onValueChange={(val) => setBatchConfigs(prev => ({
                                                ...prev,
                                                [subject.id]: { ...prev[subject.id], alternateWith: val === 'none' ? undefined : val }
                                              }))}
                                            >
                                              <SelectTrigger className="h-9">
                                                <SelectValue placeholder={t('none') || 'None'} />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">{t('none') || 'None'}</SelectItem>
                                                <SelectItem value="null">{t('oneGroupStudies')}</SelectItem>
                                                {subjects.filter(s => s.id !== subject.id).map(s => (
                                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                            <p className="text-[10px] text-slate-400">
                                              {t('alternatingSubjectsDesc')}
                                            </p>
                                          </div>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </ScrollArea>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-xl border border-amber-200">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-amber-800 mb-4">
              <LightbulbIcon className="h-5 w-5 text-amber-600" />
              {t('tipsTitle')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-amber-800">{t('tipsLinkSubjects')}</p>
                  <p className="text-sm text-amber-700">{t('tipsLinkSubjectsDesc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-amber-800">{t('tipsSetPeriods')}</p>
                  <p className="text-sm text-amber-700">{t('tipsSetPeriodsDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {lessons.length === 0 ? (
            <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
              <BookOpen className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">{t('noLessonsTitle')}</h3>
              <p className="text-slate-500 mb-6">{t('noLessonsDesc')}</p>
              <Button onClick={() => setViewMode('byClass')} className="bg-blue-600">
                <Plus className="h-4 w-4 mr-2" />
                {t('addFirstLesson') || 'Add Your First Lesson'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">{t('configuredLessons')}</h3>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  {t('lesson', { count: lessons.length })}
                </Badge>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">{t('class')}</TableHead>
                          <TableHead className="font-semibold">{t('subject')}</TableHead>
                          <TableHead className="font-semibold">{t('teacher')}</TableHead>
                          <TableHead className="font-semibold">{t('periodsWeek')}</TableHead>
                          <TableHead className="w-[80px] font-semibold text-right">{t('actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lessons.map((lesson, index) => (
                          <TableRow key={index} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{getClassName(lesson.class)}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2 items-center">
                                {getSubjectName(lesson.subject)}
                                {lesson.isGrouped && (
                                  <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600">
                                    {t('grouped')}
                                  </Badge>
                                )}
                                {lesson.alternateWith && (
                                  <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-600">
                                    {t('alternating')}
                                  </Badge>
                                )}
                                {lesson.room && (
                                  <Badge variant="outline" className="text-[10px] border-orange-200 text-orange-600">
                                    {lesson.room}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getTeacherName(lesson.teacher)}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="bg-slate-100">{lesson.hours}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => onDeleteLesson(index)}
                                className="hover:bg-red-50 hover:text-red-600 h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('duplicateConfig') || 'Duplicate Configuration'}</DialogTitle>
            <DialogDescription>
              {t('duplicateDesc') || 'Copy all lessons from'} <span className="font-bold text-blue-600">{sourceClassForDuplicate && getClassName(sourceClassForDuplicate)}</span> {t('toOtherClasses') || 'to other classes.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium mb-3 block">{t('selectTargetClasses') || 'Select target classes:'}</Label>
            <ScrollArea className="h-[300px] border rounded-md p-2">
              <div className="space-y-2">
                {classes.filter(c => c.id !== sourceClassForDuplicate).map(cls => (
                  <div key={cls.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded-md transition-colors">
                    <Checkbox
                      id={`target-${cls.id}`}
                      checked={targetClassIds.includes(cls.id)}
                      onCheckedChange={(checked) => {
                        if (checked) setTargetClassIds(prev => [...prev, cls.id])
                        else setTargetClassIds(prev => prev.filter(id => id !== cls.id))
                      }}
                    />
                    <Label htmlFor={`target-${cls.id}`} className="flex-1 cursor-pointer flex flex-wrap items-center gap-1 min-w-0">
                      <span className="truncate font-medium">{cls.name}</span>
                      {lessons.some(l => l.class === cls.id) && (
                        <span className="text-[10px] text-amber-600 whitespace-nowrap">({t('willOverwrite') || 'will overwrite existing'})</span>
                      )}
                    </Label>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicateDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={targetClassIds.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Copy className="h-4 w-4 mr-2" />
              {t('duplicateNow') || 'Duplicate Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
